import { inject, Injectable } from '@angular/core';
import DataSource from 'devextreme/data/data_source';
import { HttpClient } from '@angular/common/http';
import { map, Observable, throwError } from 'rxjs';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import { LocalStorageFacade } from '@application/localstorage';
import { IAuthAccessToken } from '@auth/data-access';
import { APP_CONFIG, resolveOriginsByConfig } from '@application/config';
import { withoutForbiddenElevation } from '@application/data-access';
import {
  IRolePermission,
  IUserRole,
  IVerifyEmail,
} from '../entities/role.interface';
import { IPerson } from '../entities/person.interface';
import { UserProfile } from '../entities/user-profile.interface';
import { IUser, TLoginCode } from '../entities/user.interface';

type TOdataResponse<T> = {
  value?: T[];
};

export type ITenantUserLookup = Pick<
  IUser,
  'PersonId' | 'FirstName' | 'TenantId'
> & {
  LastName?: string;
  FullName?: string;
};

@Injectable({ providedIn: 'root' })
export class UserService {
  private dxOdataService = inject(DxOdataStoreService);
  private http = inject(HttpClient);
  private localStorageService = inject(LocalStorageFacade);
  private readonly appConfig = inject(APP_CONFIG);

  private readonly apiUrl = this.appConfig.apiUrl;

  public getUsersFromOdata(): DataSource<any> {
    return this.dxOdataService.fromOdata<any>({
      url: this.apiUrl.personService,
      metadata: 'OData/Persons',
      key: 'PersonId',
    });
  }

  public getRoleTemplate(): Observable<IRolePermission[]> {
    return this.http.get<IRolePermission[]>(
      this.apiUrl.userService + '/Role/GetPermissionsTemplate',
      { withCredentials: true }
    );
  }

  public updateUser(payload: IUser): Observable<IUser> {
    return this.http.put<IUser>(
      this.apiUrl.userService + '/UserProfile',
      payload,
      { withCredentials: true }
    );
  }

  public getUser(personId: string): Observable<IUser> {
    return this.http.get<IUser>(
      this.apiUrl.userService + '/UserProfile?id=' + personId,
      { withCredentials: true }
    );
  }

  public getTenantUserById(
    tenantId: string,
    personId: string
  ): Observable<ITenantUserLookup | null> {
    return this.http
      .get<TOdataResponse<ITenantUserLookup>>(
        `${this.apiUrl.odata_person_service}/Users`,
        {
          params: {
            $select: 'PersonId,FirstName,LastName,FullName,TenantId',
            $filter: `(TenantId eq ${tenantId}) and (PersonId eq ${personId})`,
            $top: 1,
          },
          withCredentials: true,
        }
      )
      .pipe(map((response) => response?.value?.[0] ?? null));
  }

  public deleteUser(personId: string): Observable<IUser> {
    return this.http.delete<IUser>(
      this.apiUrl.userService + '/UserProfile?id= ' + personId,
      { withCredentials: true }
    );
  }

  public addUser(user: IUser): Observable<IUser> {
    return this.http.post<IUser>(
      this.apiUrl.userService + '/UserProfile',
      user,
      { withCredentials: true }
    );
  }

  public getRolesActivitiesOdata(): DataSource<any> {
    return this.dxOdataService.fromOdata<any>({
      url: this.apiUrl.account_service,
      metadata: 'OData/Activities',
      key: 'ActivityId',
    });
  }

  public getRolesFromOdata(): Observable<IUserRole[]> {
    return this.http
      .get<any>(this.apiUrl.userService + '/Odata/Roles', {
        withCredentials: true,
      })
      .pipe(map((_) => _.value));
  }

  public getRole(payload: string): Observable<IUserRole> {
    return this.http.get<IUserRole>(
      this.apiUrl.userService + '/Role?id=' + payload,
      { withCredentials: true }
    );
  }

  public updateRole(payload: IUserRole): Observable<IUserRole> {
    return this.http.put<IUserRole>(
      this.apiUrl.userService + '/Role',
      payload,
      { withCredentials: true }
    );
  }

  public createRole(payload: IUserRole): Observable<IUserRole> {
    return this.http.post<IUserRole>(
      this.apiUrl.userService + '/Role',
      payload,
      { withCredentials: true }
    );
  }

  public deleteRole(payload: string): Observable<any> {
    return this.http.delete<any>(
      this.apiUrl.userService + '/Role?id=' + payload,
      { withCredentials: true }
    );
  }

  public getSelfUser(): Observable<IUser> {
    const authorization = this.getAuthorization();
    if (!authorization?.PersonId) {
      return throwError(() => new Error('AUTH_REQUIRED'));
    }
    return this.http.get<IUser>(
      this.apiUrl.userService + '/UserProfile?id=' + authorization.PersonId,
      { withCredentials: true }
    );
  }

  public getSelfUserRole(): Observable<IUserRole> {
    const authorization = this.getAuthorization();
    if (!authorization?.PersonId) {
      return throwError(() => new Error('AUTH_REQUIRED'));
    }
    return this.http.get<IUserRole>(
      this.apiUrl.userService + '/Role?id=' + authorization.PersonId,
      { withCredentials: true }
    );
  }

 private getAuthorization(): IAuthAccessToken | null {
    const raw = this.localStorageService.getItem('authorization');
    try {
      return raw ? (JSON.parse(raw) as IAuthAccessToken) : null;
    } catch {
      return null;
    }
  }

  public getSelfUserProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(
      `${this.apiUrl.userService}/UserProfile/GetByAuthPersonAsync`,
      { withCredentials: true }
    );
  }

  public getAllPersonsByUser(): Observable<IUserRole[]> {
    const origins = resolveOriginsByConfig(this.appConfig);
    return this.http.get<IUserRole[]>(
      `${this.apiUrl.userService}/UserProfile/GetAllPersonsByUser`,
      {
        params: { origins },
        withCredentials: true,
      }
    );
  }

  public verifyEmail(email: string): Observable<IVerifyEmail> {
    return this.http.get<IVerifyEmail>(
      `${this.apiUrl.email_service}/VerifyEmail`,
      {
        params: { email },
        withCredentials: true,
      }
    );
  }

  public reVerifyEmail(email: string): Observable<IVerifyEmail> {
    return this.http.get<IVerifyEmail>(
      `${this.apiUrl.email_service}/ReverifyEmail`,
      {
        params: { email },
        withCredentials: true,
      }
    );
  }

  public isEmailAlreadyUsed(email): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.apiUrl.userService}/verification/IsEmailUnavailable`,
      {
        params: { email },
        withCredentials: true,
      }
    );
  }

  /**
   * Verifies if it is possible to define a temporary password for the given email.
   * @param email - Email to verify
   * @returns Observable with boolean indicating if temporary password definition is allowed
   */
  public isPossibleToSetTemporaryEmail(email: string): Observable<boolean> {
    return this.http
      .get(
        `${this.apiUrl.userService}/Verification/IsPossibleToSetTemporaryEmail`,
        {
          params: { email },
          withCredentials: true,
          responseType: 'text',
        }
      )
      .pipe(map((body) => body?.toLowerCase?.() === 'true'));
  }

  public generateLoginCode(): Observable<TLoginCode> {
    return this.http.post<TLoginCode>(
      `${this.apiUrl.userService}/Auth/GenerateLoginCode`,
      {},
      {
        context: withoutForbiddenElevation(),
        withCredentials: true,
      }
    );
  }

  public getMyAccount(id: string): Observable<IPerson> {
    return this.http.get<IPerson>(`${this.apiUrl.personService}/Person`, {
      params: { id },
      withCredentials: true,
    });
  }

  public updateMyAccount(payload: Partial<IPerson>): Observable<IPerson> {
    return this.http.patch<IPerson>(
      `${this.apiUrl.personService}/PersonWithIncludes`,
      payload,
      {
        withCredentials: true,
      }
    );
  }
}
